import React, { useRef } from "react";
import InvoiceScroll from "./InvoiceScroll";
import UsdcToken from "./UsdcToken";
import CoinStack from "./CoinStack";
import FloatingChip from "./FloatingChip";

/**
 * HeroScene — the central "creator-money diorama".
 *
 * Composition (loosely based on Luni/Omada's layered scene):
 *   • Big ghost "QUILL" wordmark behind everything
 *   • InvoiceScroll: centerpiece, slightly left of center
 *   • CoinStack: orbit cluster, right of center
 *   • UsdcToken: looping around the lower scene (stablecoin, not fiat)
 *   • 4 floating chips at the corners — feel like real product hints
 *
 * Mobile: chips drop to a 2-column row below the diorama; the token loops on
 * a smaller orbit; some decorative pieces hide.
 */
const HeroScene = () => {
  const stageRef = useRef(null);

  return (
    <div
      ref={stageRef}
      data-testid="hero-scene"
      className="relative l2-stage w-full"
      style={{
        height: "clamp(520px, 78vh, 760px)",
      }}
    >
      {/* Ghost wordmark — behind everything */}
      <div
        aria-hidden
        className="absolute inset-0 flex items-center justify-center pointer-events-none l2-ghost-wordmark select-none"
      >
        QUILL
      </div>

      {/* Floating chips — positioned around the diorama */}
      <FloatingChip
        variant="payment"
        tilt={-6}
        className="absolute hidden md:flex"
        style={{ top: "12%", left: "6%", zIndex: 5 }}
      />
      <FloatingChip
        variant="boost"
        tilt={5}
        className="absolute hidden md:flex"
        style={{ top: "20%", right: "10%", zIndex: 5 }}
      />
      <FloatingChip
        variant="split"
        tilt={-3}
        className="absolute"
        style={{ bottom: "16%", left: "8%", zIndex: 5 }}
      />
      <FloatingChip
        variant="channel"
        tilt={4}
        className="absolute"
        style={{ bottom: "10%", right: "8%", zIndex: 5 }}
      />

      {/* Central diorama — invoice + coins + bill on a 3D plane */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="relative l2-stage"
          style={{
            transform: "rotateX(8deg)",
          }}
        >
          {/* Coin stack — right side */}
          <div
            className="absolute"
            style={{
              right: "-220px",
              top: "30px",
              transform: "translateZ(20px)",
            }}
          >
            <CoinStack size={280} />
          </div>

          {/* Invoice in the center */}
          <InvoiceScroll />

          {/* USDC token — wobbles in front of the invoice, lifted forward
              and slightly right so it doesn't collide with the wax seal. */}
          <div
            className="absolute"
            style={{
              left: "calc(50% + 40px)",
              bottom: "-50px",
              transform: "translateX(-50%) translateZ(120px)",
            }}
          >
            <UsdcToken />
          </div>
        </div>
      </div>

      {/* Soft ground shadow line for the diorama */}
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          bottom: "10%",
          width: "60%",
          maxWidth: "640px",
          height: "30px",
          background:
            "radial-gradient(ellipse, rgba(31, 27, 23, 0.15) 0%, transparent 70%)",
          filter: "blur(6px)",
        }}
      />
    </div>
  );
};

export default HeroScene;
